import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { BarChart3, CalendarClock, CircleCheck, Send } from "lucide-react";
import fetchWithAuth from "../fetchWithAuth";
import { Button } from "./ui/button";
import { ChartTooltipContent } from "./ui/chart";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const metrics = [
  { key: "planned", label: "Запланировано", Icon: CalendarClock, color: "#91c7ff", fill: "#d2e8ff", style: "text-sky-700 bg-sky-50 border-sky-100" },
  { key: "sent", label: "Отправлено", Icon: Send, color: "#408dff", fill: "#aaccff", style: "text-blue-700 bg-blue-50 border-blue-100" },
  { key: "successful", label: "Успешно выполнено", Icon: CircleCheck, color: "#10b981", fill: "#d1fae5", style: "text-emerald-700 bg-emerald-50 border-emerald-100" },
];
const number = value => value == null ? "—" : new Intl.NumberFormat("ru-RU").format(value);

function useOverview(period, group, enabled = true, refreshKey) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let controller;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetchWithAuth(`/api/tasks-overview?${new URLSearchParams({ period, group })}`, { signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Не удалось загрузить статистику заданий");
        if (active) setState({ data, loading: false, error: "" });
      } catch (error) {
        if (active && error.name !== "AbortError") setState({ data: null, loading: false, error: error.message });
      }
    };
    setState({ data: null, loading: true, error: "" });
    load();
    const interval = setInterval(load, 30000);
    return () => { active = false; controller?.abort(); clearInterval(interval); };
  }, [period, group, enabled, refreshKey]);
  return state;
}

function Metrics({ summary, loading }) {
  return <div className="grid gap-3 sm:grid-cols-3">
    {metrics.map(({ key, label, Icon, style }) => <div key={key} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${style}`}>
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <div><div className="text-xs">{label}</div><div className="text-xl font-semibold tabular-nums">{loading ? "…" : number(summary?.[key])}</div></div>
    </div>)}
  </div>;
}

export function TasksToday({ refreshKey }) {
  const { data, loading, error } = useOverview("today", "all", true, refreshKey);
  return <section aria-label="Задания за сегодня" className="space-y-2">
    <p className="text-sm font-medium text-slate-600">Задания за сегодня <span className="font-normal text-slate-400">· 00:00–24:00 МСК</span></p>
    <Metrics summary={data?.summary} loading={loading} />
    {(error || data?.reportError) && <p role="status" className="text-xs text-amber-700">{error || data.reportError}</p>}
  </section>;
}

export default function TasksOverview() {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("week");
  const [group, setGroup] = useState("all");
  const [groups, setGroups] = useState([]);
  const { data, loading, error } = useOverview(period, group, open);
  useEffect(() => { if (data) setGroups(data.groups); }, [data]);
  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild>
      <Button variant="secondary" size="sm" className="gap-2 text-primary"><BarChart3 className="h-4 w-4" aria-hidden="true" />График заданий</Button>
    </DialogTrigger>
    <DialogContent className="max-h-[90vh] w-[calc(100vw_-_2rem)] max-w-4xl overflow-y-auto text-slate-900">
      <DialogHeader>
        <DialogTitle>Общий график заданий</DialogTitle>
        <DialogDescription>Запланировано → отправлено → успешно выполнено. Календарные дни по МСК.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">Группа
          <select aria-label="Группа на графике" value={group} onChange={event => setGroup(event.target.value)} className="h-9 rounded-md border bg-white px-3 text-slate-900">
            <option value="all">Все группы</option>
            {groups.map(id => <option key={id} value={id}>Группа {id}</option>)}
          </select>
        </label>
        <ToggleGroup type="single" aria-label="Период графика" value={period} onValueChange={value => value && setPeriod(value)} className="rounded-md border">
          <ToggleGroupItem value="week">Неделя</ToggleGroupItem>
          <ToggleGroupItem value="month">Месяц</ToggleGroupItem>
          <ToggleGroupItem value="quarter">3 месяца</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <Metrics summary={data?.summary} loading={loading} />
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {data?.reportError && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{data.reportError}</p>}
      {loading ? <div role="status" className="flex h-[280px] items-center justify-center text-sm text-slate-500">Загрузка графика…</div> : data && <>
        <div className="h-[280px] w-full" role="img" aria-label="График запланированных, отправленных и успешно выполненных заданий">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.days} margin={{ left: -20, right: 12, top: 12 }}>
              <CartesianGrid vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={date => date.slice(5).split("-").reverse().join(".")} tickLine={false} axisLine={false} tickMargin={8} minTickGap={28} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} tickCount={3} allowDecimals={false} />
              <Tooltip cursor={false} content={<ChartTooltipContent />} />
              {metrics.map(({ key, label, color, fill }) => <Area key={key} dataKey={key} name={label} type="monotone" fill={fill} fillOpacity={0.5} stroke={color} strokeWidth={3} isAnimationActive={false} />)}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
          {metrics.map(({ key, label, color }) => <span key={key} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />{label}</span>)}
        </div>
        <p className="text-xs text-slate-400">План и отправка — по сохранённому расписанию. Удалённые задания не учитываются.</p>
      </>}
    </DialogContent>
  </Dialog>;
}
